import { Linkable } from '@/components/blocks/Linkable';
import { ProductImage } from '@/components/blocks/ProductImage';
import { specsSx } from '@/components/content/Kit/styles/drawer';
import { STRING_TRUE, USAGE_DESCRIPTIVE } from '@/data/constants/catalog';
import { useLocalization } from '@/data/Localization';
import { ItemDetails, ResponseProductAttributeValue } from '@/data/types/Product';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, Grid, SwipeableDrawer, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { uniqWith } from 'lodash';
import { FC, useState } from 'react';
import { CDN_BASE_URL, OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

interface SearchInOtherStoreProps {
	product: ItemDetails;
}

const comparator = (l: ResponseProductAttributeValue, r: ResponseProductAttributeValue) =>
	l.value === r.value;

const capitalizeFirstLetter = (data: string): string | undefined => {
	let newData = data;
	if (!newData || newData.trim() === '') return undefined;
	newData = newData.trim().toLowerCase();
	return newData.charAt(0).toUpperCase() + newData.slice(1);
};

const emptyAttrValues: ResponseProductAttributeValue[] = [];

export const SpecsDrawer: FC<SearchInOtherStoreProps> = ({ product }) => {
	const {
		ShowProductSpecificationsKit,
		Specifications,
		SKU,
		GoToProductPageKit,
		AboutProductKit,
		TechnicalSpecificationsProductKit,
	} = useLocalization('HDMProductDetail');
	const [isOpen, setIsOpen] = useState(false);
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
	const { name, partNumber, manufacturer, href, shortDescription, attributes } = product;
	const imgURL = `${CDN_BASE_URL}/productos/${partNumber}/${partNumber}-m.jpg`;
	const nameFormatted = name.charAt(0) + name.slice(1).toLowerCase();

	const descriptiveAttributes = attributes?.filter(
		(attribute) => attribute?.displayable === STRING_TRUE && attribute?.usage === USAGE_DESCRIPTIVE
	);

	const handleOpen = () => {
		setIsOpen(true);
	};

	const handleClose = () => {
		setIsOpen(false);
	};

	return (
		<Box>
			<Typography
				variant="bodyBaseline"
				id="productSpecsTrigger"
				sx={specsSx.triggerButton}
				onClick={handleOpen}
			>
				{ShowProductSpecificationsKit.t()}
			</Typography>

			<SwipeableDrawer
				open={isOpen}
				onClose={handleClose}
				onOpen={handleOpen}
				anchor={isDesktop ? 'right' : 'bottom'}
				sx={specsSx.drawerPaperSx}
			>
				<Box sx={specsSx.headerSx}>
					<Box alignItems="center" display="flex">
						<Typography variant="headingSegment" component="h3">
							{Specifications.t()}
						</Typography>
					</Box>
					<CloseIcon sx={specsSx.closeButtonSx} onClick={handleClose} />
				</Box>
				<Divider sx={specsSx.dividerTitle} />
				<Box sx={specsSx.card}>
					<Box sx={specsSx.cardHeader}>
						<ProductImage src={imgURL} alt={name} />
						<Box sx={specsSx.nameContainer}>
							<Typography variant="bodyBaseline">
								<span style={{ fontWeight: OpenSansFontWeights.semibold }}>{manufacturer}</span>{' '}
								{nameFormatted}
							</Typography>
							<Typography variant="bodySubText" sx={specsSx.sku}>
								{SKU.t()} {partNumber}
							</Typography>
						</Box>
					</Box>
					<Divider sx={specsSx.divider} />
					<Box sx={specsSx.cardBody}>
						<Typography variant="headingSegment">{AboutProductKit.t()}</Typography>
						<Divider sx={specsSx.divider} />
						<Typography variant="bodyBaseline">{shortDescription}</Typography>
						<Typography variant="headingSegment" sx={specsSx.marginTop}>
							{TechnicalSpecificationsProductKit.t()}
						</Typography>
						<Divider sx={specsSx.divider} />
						<Grid container data-testid="kit-item-specs" id="kit-item-specs" spacing={1.25}>
							{descriptiveAttributes?.map(
								({ identifier, name, values = emptyAttrValues }, index) => {
									const isOddRow = Math.floor(index / 1) % 2 !== 0;
									const rowBackgroundColor = isOddRow
										? theme.palette.common.white
										: theme.palette.grey[50];

									const attributeValue = uniqWith(values, comparator)
										.map((v) => v.value)
										.join(', ');

									const displayAttributeName = capitalizeFirstLetter(name) || name;
									const displayAttributeValue =
										capitalizeFirstLetter(attributeValue) || attributeValue;

									return (
										<Grid item xs={12} key={`item-attribute-${identifier}`}>
											<Box
												sx={{
													...specsSx.contentBox,
													backgroundColor: rowBackgroundColor,
												}}
											>
												<Box sx={specsSx.attribute}>
													<Typography
														variant="bodyBaseline"
														component="p"
														fontWeight={OpenSansFontWeights.semibold}
													>
														{displayAttributeName}
													</Typography>
												</Box>
												<Box sx={specsSx.attribute}>
													<Typography variant="bodyBaseline" component="p">
														{displayAttributeValue}
													</Typography>
												</Box>
											</Box>
										</Grid>
									);
								}
							)}
						</Grid>
						<Linkable href={href} id={href} data-testid={href} sx={specsSx.marginTop}>
							<Typography variant="bodyBaseline" id="productSpecsLink" sx={specsSx.link}>
								{GoToProductPageKit.t()}
							</Typography>
						</Linkable>
					</Box>
				</Box>
			</SwipeableDrawer>
		</Box>
	);
};
